import React, { useContext, useEffect, useState } from 'react';
import IPage from '../../../interfaces/IPage';
import { Container } from './EmplyeeSearch.styped';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import IEmployee from '../../../interfaces/IEmployee';
import IFormError from '../../../interfaces/IFormError';
import { useEmployeeService } from '../../../hooks/services/useEmployeeService';
import debounce from 'lodash.debounce';
import * as yup from 'yup';
import PageTitle from '../../../components/page-title/PageTitle';
import { Autocomplete, Box, Button, CircularProgress, LinearProgress, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ThemeOptionContext from '../../../context/theme-option/ThemeOptionContext';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface IProps extends IPage {}

const EmployeeSearch = (props: IProps) => {
  window.document.title = props.title;

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly IEmployee[]>([]);
  const loadingInput = open;

  const [employee, setEmployee] = useState<IEmployee | null>(null);
  const request = useEmployeeService();

  const { state } = useContext(UserContext);

  const toastr = useToastrVanila();
  const validation = useValidateSchema();
  const [errors, setErrors] = useState<IFormError | null>();
  const navigate = useNavigate();

  const { state: themeState } = useContext(ThemeOptionContext);

  useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await loadEmployees(' - ');
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingInput]);

  useEffect(() => {
    setErrors(null);
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const loadEmployees = debounce(async (filter = ' ') => {
    setLoading(true);
    await request
      .listForMedicalCertificateSearch(filter)
      .then(employees => {
        setOptions(employees);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(`Erro ao carregar os dados: ${error.message}`);
        setLoading(false);
      });
  }, 500);

  const schema = yup.object({
    cpf: yup
      .string()
      .required('Campo obrigatório')
      .min(1, 'Campo obrigatório.'),
  });

  const hancleClick = async () => {
    const result = await validation.validate(schema, employee || {});
    setErrors(result.errors);

    if (!result.isValid) {
      return;
    }
    if(hasAccessToAction(["cadastrar-documento-atestado-web"], state.resourcesWithActions, "Editar")) {
      navigate(`/cadastrar-documento/0/${employee?.id}/new`);
    } else {
      toastr.error('Acesso negado. Verificar com o administrador do sistema.');
    }
  };

  return (
    <Container>
      <PageTitle title="CADASTRO DE ATESTADO" />
      {loading && <LinearProgress />}
      <Box
        sx={{
          height: '100%',
        }}
      >
        <Autocomplete
          open={open}
          value={employee}
          onChange={(e: any, newValue) => {
            const castedEmployee = { ...newValue } as IEmployee;
            setEmployee({ ...castedEmployee });
          }}
          onOpen={() => {
            setOpen(true);
            setEmployee(null);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option, value) => {
            return option?.name === value?.name;
          }}
          renderOption={(props, option) => (
            <li {...props}>
              <Box
                sx={{
                  flexGrow: 1,
                  '& span': {
                    color: option.situation === 7 ? '#c0392b' : themeState.isDark ? '#F8FFFF' : '#212121',
                  },
                }}
              >
                <span>
                  {option?.cpf
                    ? `${option.cpf} - ${option.code} - ${option.name} ${option.situation === 7 ? ' - Demitido' : ''}`
                    : ''}
                </span>
              </Box>
            </li>
          )}
          getOptionLabel={option =>
            option?.cpf
              ? `${option.cpf} - ${option.code} -  ${option.name} ${option.situation === 7 ? ' - Demitido' : ''}`
              : ''
          }
          options={options}
          loading={loading}
          renderInput={params => (
            <TextField
              {...params}
              sx={{ marginTop: '30px' }}
              label="Pesquisar por nome, matricula ou CPF"
              error={Boolean(errors?.cpf)}
              helperText={errors?.cpf}
              onChange={e => {
                loadEmployees(e.target.value);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                style: {
                  color: employee && employee.situation === 7 ? '#c0392b' : themeState.isDark ? '#F8FFFF' : '#212121',
                },
              }}
            />
          )}
        />

        <Box sx={{ padding: '50px 0px 10px 0px' }}>
          <Button variant="contained" onClick={hancleClick}>
            Cadastrar
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default EmployeeSearch;
